import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../config/api';

export interface GalleryItem {
  _id: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  authorId: string;
  authorName: string;
  instanceUrl?: string;
  repositoryUrl?: string;
  media: GalleryMedia[];
  voteCount: number;
  viewCount: number;
  hasVoted?: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface GalleryMedia {
  _id: string;
  url: string;
  thumbnailUrl?: string;
  type: 'image' | 'video';
  filename: string;
  order: number;
}

export interface GalleryCategory {
  _id: string;
  name: string;
  slug: string;
  count: number;
}

export interface GalleryListParams {
  page?: number;
  limit?: number;
  category?: string;
  tag?: string;
  search?: string;
  sort?: string;
}

export interface GalleryListResponse {
  items: GalleryItem[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateGalleryItemData {
  title: string;
  description: string;
  category: string;
  tags: string[];
  instanceUrl?: string;
  repositoryUrl?: string;
  authorName?: string;
  authorAvatarUrl?: string;
}

export interface ReportData {
  reason: string;
  description?: string;
}

// Map 'id' from PostgreSQL backend to '_id' used in frontend
const normalizeItem = (item: any): GalleryItem => {
  if (item.id && !item._id) item._id = item.id;
  if (item.userId && !item.authorId) item.authorId = item.userId;
  if (item.media) {
    item.media = item.media.map((m: any) => {
      if (m.id && !m._id) m._id = m.id;
      return m;
    });
  }
  return item;
};

export const fetchGalleryItems = async (
  params: GalleryListParams
): Promise<GalleryListResponse> => {
  const response = await apiClient.get(API_ENDPOINTS.GALLERY_LIST, { params });
  const data = response.data;
  if (data.items) data.items = data.items.map(normalizeItem);
  return data;
};

export const fetchGalleryItem = async (id: string): Promise<GalleryItem> => {
  const response = await apiClient.get(API_ENDPOINTS.GALLERY_ITEM(id));
  return normalizeItem(response.data);
};

export const createGalleryItem = async (
  data: CreateGalleryItemData
): Promise<GalleryItem> => {
  const response = await apiClient.post(API_ENDPOINTS.GALLERY_LIST, data);
  return normalizeItem(response.data);
};

export const updateGalleryItem = async (
  id: string,
  data: Partial<CreateGalleryItemData>
): Promise<GalleryItem> => {
  const response = await apiClient.put(API_ENDPOINTS.GALLERY_ITEM(id), data);
  return response.data;
};

export const deleteGalleryItem = async (id: string): Promise<void> => {
  await apiClient.delete(API_ENDPOINTS.GALLERY_ITEM(id));
};

export const voteGalleryItem = async (
  id: string
): Promise<{ voteCount: number; hasVoted: boolean }> => {
  const response = await apiClient.post(API_ENDPOINTS.GALLERY_VOTE(id));
  return response.data;
};

export const reportGalleryItem = async (
  id: string,
  data: ReportData
): Promise<void> => {
  await apiClient.post(API_ENDPOINTS.GALLERY_REPORT(id), data);
};

export const uploadMedia = async (
  galleryItemId: string,
  files: File[]
): Promise<GalleryMedia[]> => {
  const formData = new FormData();
  formData.append('galleryItemId', galleryItemId);
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = await apiClient.post(API_ENDPOINTS.MEDIA_UPLOAD, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const fetchCategories = async (): Promise<GalleryCategory[]> => {
  const response = await apiClient.get(API_ENDPOINTS.GALLERY_CATEGORIES);
  return response.data;
};
